// What the server keeps for a group, encrypted: the "save everyone" link's contents and the group
// photo. Each has a token: 16 letters and digits, so links stay short and survive every Messages
// app's link detection. The token never reaches the server; it derives three values:
//   id     where the encrypted copy is kept
//   key    AES-256-GCM key for the contents
//   write  proof that a link update comes from someone holding the token
// Links are encrypted here. A photo goes up as-is with its key, because the server finds the faces
// in it; it encrypts the photo with that key and keeps neither.

const B62 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
export const TOKEN_LENGTH = 16 // ~95 bits

export function newToken() {
  let out = ''
  while (out.length < TOKEN_LENGTH) {
    for (const b of crypto.getRandomValues(new Uint8Array(TOKEN_LENGTH * 2))) {
      if (b < 248 && out.length < TOKEN_LENGTH) out += B62[b % 62] // 248 = 4 * 62, so no bias
    }
  }
  return out
}

export const isToken = (s) => typeof s === 'string' && /^[0-9A-Za-z]{16}$/.test(s)

const utf8 = (s) => new TextEncoder().encode(s)
const b64url = (bytes) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

const derived = new Map()
export function derive(token) {
  if (!derived.has(token)) {
    derived.set(token, (async () => {
      const base = await crypto.subtle.importKey('raw', utf8(token), 'HKDF', false, ['deriveBits', 'deriveKey'])
      const params = (info) => ({ name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: utf8(`groupchat ${info}`) })
      const bits = async (info) => b64url(new Uint8Array(await crypto.subtle.deriveBits(params(info), base, 128)))
      const raw = new Uint8Array(await crypto.subtle.deriveBits(params('key'), base, 256))
      return {
        id: await bits('id'),
        write: await bits('write'),
        key: await crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']),
        rawKey: b64url(raw),
      }
    })())
  }
  return derived.get(token)
}

export async function seal(token, bytes) {
  const { key } = await derive(token)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, bytes))
  const out = new Uint8Array(12 + ct.length)
  out.set(iv)
  out.set(ct, 12)
  return out
}

export async function unseal(token, data) {
  const { key } = await derive(token)
  return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: data.subarray(0, 12) }, key, data.subarray(12)))
}

// A link: anyone with the token can replace it.
export async function putLink(token, value, { keepalive = false } = {}) {
  const { id, write } = await derive(token)
  const res = await fetch(`/api/link/${id}`, {
    method: 'PUT',
    body: await seal(token, utf8(JSON.stringify(value))),
    keepalive,
    headers: { 'Content-Type': 'application/octet-stream', 'X-Write-Key': write },
  })
  if (!res.ok) throw new Error(`Couldn't save (${res.status})`)
}

// A photo, written once. Returns {w, h, faces} from the server.
export async function putPhoto(token, jpegBytes) {
  const { id, rawKey } = await derive(token)
  const res = await fetch(`/api/photo/${id}`, {
    method: 'PUT',
    body: jpegBytes,
    headers: { 'Content-Type': 'image/jpeg', 'X-Photo-Key': rawKey },
  })
  if (!res.ok) throw new Error(res.status === 415 ? "That photo didn't work" : `Couldn't save the photo (${res.status})`)
  return res.json()
}

// The decrypted bytes, or null if there's nothing there (never saved, or expired).
export async function get(kind, token) {
  const { id } = await derive(token)
  const res = await fetch(`/api/${kind}/${id}`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`Couldn't load (${res.status})`)
  return unseal(token, new Uint8Array(await res.arrayBuffer()))
}

export const getJson = async (kind, token) => {
  const bytes = await get(kind, token)
  return bytes && JSON.parse(new TextDecoder().decode(bytes))
}
